// Hero cleanup -- the destination for the hero's new "Apply for Early
// Access" CTA. A genuine application record for manual review (POST
// /api/early-access/apply, server.py), not an instant account -- that's
// a deliberately different thing from /login's real, working signup
// flow. Unguarded like LandingPage/SnapshotPage: reachable whether
// logged in or not, no redirect logic needed.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from '@phosphor-icons/react';
import { earlyAccessAPI } from '@/lib/api';

export default function EarlyAccessPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', website: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      await earlyAccessAPI.apply(form);
      setStatus('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong — please try again.');
      setStatus('error');
    }
  }

  return (
    <div className="min-h-screen bg-[var(--warm-canvas)]" data-testid="early-access-page">
      <header className="border-b border-[var(--warm-border)] px-6 sm:px-10 py-5">
        <Link to="/" className="font-display text-[16px] font-extrabold text-[var(--text-primary)]" data-testid="early-access-logo">
          SAHIB
        </Link>
      </header>

      <main className="max-w-lg mx-auto px-6 sm:px-10 py-16 sm:py-24">
        {status === 'done' ? (
          <div className="text-center" data-testid="early-access-success">
            <CheckCircle size={40} weight="fill" className="text-[var(--success)] mx-auto mb-5" />
            <h1 className="font-display font-extrabold text-[var(--text-primary)] text-[26px] mb-3">Application received.</h1>
            <p className="text-[14.5px] text-[var(--text-secondary)] leading-relaxed">
              We review applications on a rolling basis and will reach out at the email you provided.
            </p>
            <Link to="/" className="inline-block mt-8 text-[13.5px] font-semibold text-[var(--text-primary)] underline">
              Back to Martian
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display font-extrabold text-[var(--text-primary)] text-balance" style={{ fontSize: 'clamp(1.7rem, 1.4rem + 1.4vw, 2.4rem)', lineHeight: 1.18 }}>
              Apply for early access.
            </h1>
            <p className="mt-4 text-[14.5px] text-[var(--text-secondary)] leading-relaxed">
              Tell us about your brand. We're reviewing applications on a rolling basis as we bring on early teams.
            </p>

            <form onSubmit={handleSubmit} className="mt-9 space-y-4" data-testid="early-access-form">
              <div>
                <label htmlFor="ea-name" className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Full name</label>
                <input id="ea-name" data-testid="early-access-name" type="text" required value={form.name} onChange={update('name')}
                  className="w-full border border-[var(--border)] rounded-[var(--radius-button)] px-4 py-3 text-[15px] text-[var(--text-primary)] bg-[var(--surface)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)]" />
              </div>
              <div>
                <label htmlFor="ea-email" className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Work email</label>
                <input id="ea-email" data-testid="early-access-email" type="email" required value={form.email} onChange={update('email')}
                  className="w-full border border-[var(--border)] rounded-[var(--radius-button)] px-4 py-3 text-[15px] text-[var(--text-primary)] bg-[var(--surface)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)]" />
              </div>
              <div>
                <label htmlFor="ea-company" className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Company</label>
                <input id="ea-company" data-testid="early-access-company" type="text" required value={form.company} onChange={update('company')}
                  className="w-full border border-[var(--border)] rounded-[var(--radius-button)] px-4 py-3 text-[15px] text-[var(--text-primary)] bg-[var(--surface)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)]" />
              </div>
              <div>
                <label htmlFor="ea-website" className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Website <span className="font-normal text-[var(--text-tertiary)]">(optional)</span></label>
                <input id="ea-website" data-testid="early-access-website" type="text" value={form.website} onChange={update('website')} placeholder="yourbrand.com"
                  className="w-full border border-[var(--border)] rounded-[var(--radius-button)] px-4 py-3 text-[15px] text-[var(--text-primary)] bg-[var(--surface)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)]" />
              </div>

              {error && <p className="text-[13px] text-[var(--error)]" data-testid="early-access-error">{error}</p>}

              <button type="submit" disabled={status === 'submitting'} data-testid="early-access-submit"
                className="w-full bg-[var(--text-primary)] hover:bg-[var(--text-secondary)] text-white font-semibold text-[15px] px-6 py-3.5 rounded-[var(--radius-button)] transition-colors disabled:opacity-60">
                {status === 'submitting' ? 'Submitting…' : 'Submit application →'}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
